// Contracts — плагин размножения Dev-отдела (Figma).
// Воркер: apps/work-api/src/devplugin.ts. Клиент: figma_plugin_dev/.
//
// Плагин — отдельный клиент work-воркера по образу дизайнерского плагина
// pothos: бандл скачивается из кабинета (Firebase + capability `dev`) с
// врезанным per-user токеном, дальше все вызовы идут по заголовку
// `X-Dev-Plugin-Token`. Формы сняты с migrations/0035 — не выдуманы.
//
// Принцип клиента (зафиксирован владельцем): серверные данные — только
// ДЕФОЛТЫ. Локальные правки размножителя под конкретную площадку живут в
// плагине (clientStorage + экспорт/импорт) и сервером никогда не
// перезаписываются. Контракт локального слоя здесь намеренно отсутствует.
import { z } from 'zod';
import { DevKindSchema } from './dev';

// ─── GET /api/work/dev/pluginToken ────────────────────────────────────────
// Токен плагина, отданный ТЕКСТОМ. Нужен, потому что до этого он существовал
// только запечённым внутрь зипа dev-плагина: чтобы вставить его в tick, человек
// лез в code.js чужого архива и копировал строку глазами.
export const DevPluginTokenResSchema = z.object({
  ok: z.boolean(),
  token: z.string(),
  rotated: z.boolean(),
});
export type DevPluginTokenRes = z.infer<typeof DevPluginTokenResSchema>;

// ─── шрифт мастера ────────────────────────────────────────────────────────
export const DevPluginFontSchema = z.object({
  family: z.string(),
  style: z.string().default(''),
  /** Есть ли файл на сервере (можно скачать через /plugin/font). */
  downloadable: z.boolean().default(false),
});
export type DevPluginFont = z.infer<typeof DevPluginFontSchema>;

// ─── карточка в плагине ───────────────────────────────────────────────────
// В плагин попадают ТОЛЬКО карточки, готовые к размножению: есть площадка
// (имя), есть манифест И есть мастер от дизайнера. Остальные не показываются
// вовсе — это продуктовое решение, а не фильтр по умолчанию.
export const DevPluginCardSchema = z.object({
  id: z.string(),
  locale: z.string(),
  kind: DevKindSchema,
  /** Категория материала для каталога: gift_card | coupon | event_ticket. */
  category: z.enum(['gift_card', 'coupon', 'event_ticket']),
  /** Площадка (агрегатор). */
  source: z.string(),
  round: z.union([z.literal(1), z.literal(2)]),
  /** Сколько нужно сделать ЭТИМ заходом (остаток квоты на момент захода). */
  target: z.number().int().nonnegative(),
  /** Сколько уже отгружено из плагина по этой карточке. */
  shipped_from_plugin: z.number().int().nonnegative().default(0),
  /** Сколько позиций в манифесте (по merchants/строкам — считает сервер). */
  manifest_count: z.number().int().nonnegative().default(0),
  manifest_version: z.number().int().nonnegative().default(0),
  manifest_file: z.string().default(''),
  master_version: z.number().int().nonnegative().default(0),
  master_file: z.string().default(''),
  fonts: z.array(DevPluginFontSchema).default([]),
  /** Открытые флаги этой карточки (manifest/design) — чтобы не дублировать. */
  open_flags: z.array(z.enum(['manifest', 'design'])).default([]),
  note: z.string().default(''),
  updated_at: z.string().default(''),
});
export type DevPluginCard = z.infer<typeof DevPluginCardSchema>;

// ─── GET /api/work/dev/plugin/cards ───────────────────────────────────────
export const DevPluginCardsResSchema = z.object({
  ok: z.boolean(),
  cards: z.array(DevPluginCardSchema),
  /** Кто мы (для подписи в UI). */
  email: z.string().default(''),
});
export type DevPluginCardsRes = z.infer<typeof DevPluginCardsResSchema>;

// ─── GET /api/work/dev/plugin/manifest?card_id= ──────────────────────────
// Бинарный ответ (байты манифеста) + журнал dev_activity в том же запросе.
export const DevPluginArtifactQuerySchema = z.object({
  card_id: z.string().trim().min(1).max(64),
});

// ─── GET /api/work/dev/plugin/master?card_id= ────────────────────────────
// Бинарный ответ (мастер-кит от дизайнера: PNG или zip PNG+JSON).
// Контракт — только query; поток описан в docs/flows/work-dev-plugin.md.

// ─── GET /api/work/dev/plugin/font?card_id=&family= ──────────────────────
export const DevPluginFontQuerySchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  family: z.string().trim().min(1).max(120),
});

// ─── POST /api/work/dev/plugin/flag ──────────────────────────────────────
// Возврат работы: 'manifest' → Dev-команде (карточка возвращается на этап
// manifest), 'design' → дизайнеру. Комментарий обязателен: возврат без
// причины неотличим от случайного клика.
export const DevPluginFlagReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  target: z.enum(['manifest', 'design']),
  comment: z.string().trim().min(3).max(2000),
});
export type DevPluginFlagReq = z.infer<typeof DevPluginFlagReqSchema>;

export const DevPluginFlagResSchema = z.object({ ok: z.boolean(), flag_id: z.string() });
export type DevPluginFlagRes = z.infer<typeof DevPluginFlagResSchema>;

// ─── отгрузка пачками ─────────────────────────────────────────────────────
// begin → N×frame (сырые байты PNG, метаданные в заголовках X-Batch-Id /
// X-File-Name / X-Code-Value / X-Code-Type) → complete. Пачка ~100 кадров.
// Каждый принятый кадр СРАЗУ индексируется в pothos_assets
// (source_pipeline='work-dev', status='qa') — как обычные пути загрузки.
export const DevPluginShipBeginReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  /** Сколько кадров собираемся прислать в этой пачке. */
  expected: z.number().int().positive().max(500),
});
export type DevPluginShipBeginReq = z.infer<typeof DevPluginShipBeginReqSchema>;

export const DevPluginShipBeginResSchema = z.object({
  ok: z.boolean(),
  batch_id: z.string(),
});
export type DevPluginShipBeginRes = z.infer<typeof DevPluginShipBeginResSchema>;

// POST /api/work/dev/plugin/ship/frame — бинарный вход (см. заголовки выше);
// контракт покрывает только ответ.
export const DevPluginShipFrameResSchema = z.object({
  ok: z.boolean(),
  frame_id: z.string(),
  /** Дубль кода внутри карточки — кадр не принят, перегенерировать. */
  duplicate: z.boolean().default(false),
});
export type DevPluginShipFrameRes = z.infer<typeof DevPluginShipFrameResSchema>;

export const DevPluginShipCompleteReqSchema = z.object({
  card_id: z.string().trim().min(1).max(64),
  batch_id: z.string().trim().min(1).max(64),
});
export type DevPluginShipCompleteReq = z.infer<typeof DevPluginShipCompleteReqSchema>;

export const DevPluginShipCompleteResSchema = z.object({
  ok: z.boolean(),
  received: z.number().int().nonnegative(),
  indexed: z.number().int().nonnegative(),
  expected: z.number().int().nonnegative(),
});
export type DevPluginShipCompleteRes = z.infer<typeof DevPluginShipCompleteResSchema>;

// ─── статус eval исходников (для кабинета и плагина) ─────────────────────
// GET /api/work/dev/plugin/sourceEval?card_id=
export const DevSourceEvalResSchema = z.object({
  ok: z.boolean(),
  total: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  error: z.number().int().nonnegative(),
  bridged: z.number().int().nonnegative(),
});
export type DevSourceEvalRes = z.infer<typeof DevSourceEvalResSchema>;
